/**
 * Module Staff #01 — Tableau de bord admin & navigation
 * Source : Guide_Admin_Baobab_Connect.pdf · Chapitre 1 (Démarrage rapide admin)
 */
const moduleStaffDashboard = {
  id: 'staff-dashboard-admin',
  population: 'staff',
  order: 1,
  category: 'Pilotage',
  title: 'Tableau de bord admin',
  subtitle: 'Naviguer efficacement dans le back-office et exploiter les KPIs stratégiques',
  icon: 'BarChart3',
  duration: '20 min',
  level: 'Débutant',
  audience: 'Tout staff interne',
  pdfUrl: '/api/uploads/docs/training/staff-dashboard-admin.pdf',
  slides: [
    { type: 'cover', eyebrow: 'Module 01 — Staff', title: 'Tableau de bord admin', subtitle: 'Votre cockpit interne quotidien', icon: 'BarChart3', duration: '20 min', audience: 'Staff interne', level: 'Débutant' },
    { type: 'intro', title: 'Objectifs', intro: 'En tant que membre du staff Baobab, le tableau de bord admin est votre poste de pilotage. Il agrège tous les flux entrants et indicateurs stratégiques.', objectives: [
      'Comprendre l\'organisation par onglets (Vue d\'ensemble, KPIs financiers, Opérationnels...)',
      'Naviguer dans la sidebar par catégorie (Opérations, Finance, Direction, Admin)',
      'Lire les KPIs stratégiques : Croissance, ARPU, Conversion, Rétention',
      'Utiliser les KPIs cliquables pour drill-down vers les opérations',
      'Identifier vos accès selon votre rôle (CFO, opérateur, admin, customer success)',
    ]},
    { type: 'concept', eyebrow: 'Organisation', icon: 'BookOpen', title: 'Les 4 zones du dashboard admin',
      lede: 'Le dashboard est organisé en zones fonctionnelles pour faciliter la lecture :',
      bullets: [
        '🟢 Pipeline Opérationnel : nouveaux KPIs cliquables (Total, En prépa, En exécution, Terminées, Annulées)',
        '👑 KPIs Stratégiques : Croissance MoM/YoY, ARPU, Conversion, Rétention, Concentration Top 10',
        '🎯 Objectifs Business : CA mensuel/trim/annuel avec jauge d\'atteinte',
        '⚙️ KPIs Opérationnels : temps moyen de traitement, taux d\'annulation, ops sans BL...',
      ],
      note: 'Les onglets en haut permettent de basculer entre les vues selon votre rôle (Direction, Finance, Opérations).',
    },
    { type: 'screenshot', eyebrow: 'Pipeline Opérationnel', title: 'La nouvelle section Pipeline (cliquable)',
      callouts: [
        { label: 'Opérations totales', text: 'Clic → liste complète /admin/operations.', x: 12, y: 35 },
        { label: 'En préparation', text: 'Devis, validation, négo en attente. Clic → filtre auto.', x: 30, y: 35 },
        { label: 'En exécution', text: 'Chargement, transit, livraison. Clic → filtre auto.', x: 48, y: 35 },
        { label: 'Terminées', text: 'Livrées + payées. Clic → ?status=terminee.', x: 66, y: 35 },
        { label: 'Annulées', text: 'Par client ou admin. Drill-down disponible.', x: 84, y: 35 },
      ],
    },
    { type: 'steps', title: 'Rituel matinal du staff (10 min)', steps: [
      { title: 'Ouvrir le dashboard admin', detail: 'Première chose à faire en arrivant.' },
      { title: 'Scanner le Pipeline Opérationnel', detail: 'Y a-t-il des opérations bloquées en préparation ?' },
      { title: 'Vérifier les alertes en bandeau', detail: 'Bandeau rouge = action urgente requise.' },
      { title: 'Onglet KPIs Stratégiques', detail: 'Croissance MoM : suis-je dans la cible mensuelle ?' },
      { title: 'Drill-down sur l\'anomalie', detail: 'Cliquer sur le KPI rouge pour comprendre.' },
      { title: 'Distribuer les actions à l\'équipe', detail: 'Assigner opérations ou tickets en attente.' },
    ]},
    { type: 'concept', eyebrow: 'Sidebar', icon: 'BookOpen', title: 'La navigation latérale par catégorie',
      lede: 'La sidebar est organisée pour refléter les responsabilités métier :',
      bullets: [
        '🚛 Opérations : Demandes, opérations, négociations, bons de chargement',
        '💰 Finance : Cockpit CFO, Audit factures, Recouvrement, Rapports',
        '👥 Clients : Liste, contrats, customer success, NPS',
        '⚙️ Administration : Utilisateurs, RBAC, Configuration, Audit',
        '📚 Documentation : Guides, formations, ressources',
      ],
      note: 'Vos accès dépendent de votre rôle. Si vous ne voyez pas une section, c\'est normal (RBAC).',
    },
    { type: 'screenshot', eyebrow: 'Drill-down KPIs', title: 'Cliquer sur un KPI ouvre la liste filtrée',
      callouts: [
        { label: 'Avant le clic', text: 'KPI "En exécution : 8" sur le dashboard.', x: 20, y: 25 },
        { label: 'Après le clic', text: 'URL devient /admin/operations?status=execution.', x: 55, y: 25 },
        { label: 'Filtre auto-appliqué', text: 'L\'onglet phase "Exécution" est actif, 8 résultats affichés.', x: 50, y: 55 },
      ],
    },
    { type: 'comparison', title: 'Bonnes pratiques de navigation', do: [
      'Garder le dashboard ouvert dans un onglet permanent',
      'Configurer les notifications pour les alertes critiques',
      'Utiliser les KPIs cliquables comme raccourcis quotidiens',
      'Sauvegarder vos vues filtrées dans /admin/operations',
      'Vérifier les onglets Pipeline + Stratégique chaque matin',
    ], dont: [
      'Ne pas tout chercher manuellement via la sidebar',
      'Ne pas ignorer les bandeaux d\'alerte rouge',
      'Ne pas modifier les KPIs cibles sans alignement direction',
      'Ne pas demander à l\'équipe Tech ce qui est dans le dashboard',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Le KPI "Annulées" indique 5 cette semaine. Quelle est votre première action ?',
      options: [
        'Demander à la direction d\'augmenter l\'objectif',
        'Cliquer sur le KPI pour voir les opérations concernées et analyser les motifs',
        'Envoyer un message global à tous les clients',
        'Modifier le filtre temporel',
      ],
      answerIndex: 1,
      explanation: 'Le drill-down vous donne accès aux opérations annulées avec leur motif. C\'est la base pour identifier un pattern (problème prix, opérateur, marchandise...).',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Vous êtes Customer Success Manager. Quelle section de la sidebar utilisez-vous le plus ?',
      options: [
        'Finance > Audit Factures',
        'Clients > Customer Success + NPS',
        'Administration > Utilisateurs',
        'Opérations > Bons de chargement',
      ],
      answerIndex: 1,
      explanation: 'Le CSM se concentre sur l\'expérience client : suivi NPS, alertes churn, satisfaction. Les autres sections vous seront restreintes (RBAC).',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'Le dashboard admin n\'est pas un rapport, c\'est un cockpit d\'action.',
      'Les KPIs cliquables sont vos raccourcis quotidiens.',
      'La sidebar reflète votre rôle métier (RBAC), pas tout le système.',
      'Onglets stratégiques pour direction, Pipeline pour opérationnels.',
      'Rituel matinal de 10 min suffit pour piloter au quotidien.',
    ]},
    { type: 'cta', title: 'Module 01 terminé !', subtitle: 'Vous savez naviguer dans le back-office. Continuez avec « Gestion des opérations » ou « Cockpit CFO ».',
      actions: [
        { label: 'Ouvrir le dashboard admin', href: '/admin/dashboard' },
        { label: 'Module Cockpit CFO', href: '/docs/admin/training/staff-cockpit-cfo' },
        { label: 'Catalogue', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffDashboard;
